/**
 * `workflowRunUpdateNote.ts` — the line Spark's conversation gets when the
 * LEARNER moves a guided run with their own panel (web-only; no Unity
 * counterpart).
 *
 * The run-state bullets in `# Runtime Conversation Instructions` already say
 * where the run is, and they are pushed on every move. That was not enough:
 * the model answers from its CONVERSATION as much as from its instructions,
 * and a move the learner makes on their panel left nothing in the
 * conversation. Measured 2026-09-11 — after two outline picks, "can you replay
 * the step?" re-sent the agent's own last named jump, 80 s old, over bullets
 * that correctly named the step the learner was on. The conversation's last
 * word on position was that tool reply.
 *
 * So a learner's move is also SAID, as one line posted with
 * `sendContextNote`: read by the model, never answered, never shown in the
 * chat, never recorded as a question. An agent's move gets no note — its tool
 * reply already says where it took the learner, and saying it twice is noise.
 * `public/AI/roles/guide.md` teaches the Guide what the prefix means.
 *
 * A run STARTING is said too, whoever started it (AI-1419). The conversation
 * outlives a run: after one finished through Spark, its `run_finished` reply
 * and Spark's own "the training is finished" were still the conversation's
 * last word on the run, and through the next run's Welcome the Guide kept
 * saying it was over — "you'll need to launch it from your side".
 */

import type {
  WorkflowRunStart,
  WorkflowStepArrival,
} from "@models/workflow/IWorkflowRunModel";

/**
 * The prefix `guide.md` names. Changing it means changing the role prompt in
 * the same edit, or the Guide meets a line it has no instruction for.
 */
export const K_RUN_UPDATE_PREFIX = "Run update:";

/**
 * The note for one arrival, or null when there is nothing to say — every move
 * the AGENT made. Names no tool: it is a fact about the run, and the role that
 * reads it may not hold the workflow tools (the account can withdraw the Guide).
 */
export function buildWorkflowRunUpdateNote(arrival: WorkflowStepArrival): string | null {
  const { cause } = arrival;
  if (cause.by !== "learner") return null;

  let action: string;
  switch (cause.via) {
    case "next":
      action = arrival.fromStepName
        ? `pressed Next, completing "${arrival.fromStepName}"`
        : "pressed Next";
      break;
    case "back":
      action = "pressed Back";
      break;
    case "outline":
      action = "picked a step from their outline";
      break;
    case "trigger":
      action = `pressed the "${cause.label}" button`;
      break;
  }

  return (
    `${K_RUN_UPDATE_PREFIX} the learner ${action}; they are now on ` +
    `"${arrival.stepName}" (${arrival.position} of ${arrival.stepCount}).`
  );
}

/**
 * The note for a run starting. Always said: unlike an agent's step move, a
 * start leaves the Guide no tool reply of its own to stand in for it — Play
 * and autostart have none, and `start_workflow` belongs to the author roles.
 *
 * Its second sentence is what closes the bug, not decoration: the model reads
 * a new run's state bullets and still believes its own earlier "the training
 * is finished", so the note has to say which run that belonged to. It
 * describes the conversation, not the learner, so there is nothing in it for
 * the Guide to repeat to them.
 */
export function buildWorkflowRunStartNote(start: WorkflowRunStart): string {
  return (
    `${K_RUN_UPDATE_PREFIX} a new run of the guided training "${start.workflowName}" has started; ` +
    `the learner is on "${start.stepName}" (${start.position} of ${start.stepCount}). ` +
    "Anything earlier in this conversation about the training finishing or stopping was about " +
    "a previous run - this one is playing now."
  );
}
